import React, { useEffect, useState } from 'react';
import ReactFullpage from '@fullpage/react-fullpage';
import qs from 'query-string';
import { InView } from 'react-intersection-observer';
import { MessagesInitializer } from './comps/messages/initializer';
import DebriefingSection from './comps/layout/debriefing-section';
import TeamSelector from './comps/team-selector';
import useKeyPress from './hooks/useKeyPress';
import PresentationToolbar from './comps/presentation-toolbar';
import NavigationToolbar from './comps/navigation-toolbar';
import PerformanceAdjustmentSection from './comps/sections/performance-adjustment-section';
import ARBudgetByScenarioSection from './comps/sections/ar-budget-by-scenario-section';
import DistanceDispersionSection from './comps/sections/distance-dispersion-section';
import StepsQualitySection from './comps/sections/steps-quality-section';
import './styles.css';
import Bho from './comps/plots/bho';
import PlanAnalyzerSection from './comps/sections/plan-analyzers-section';
import buildAssetPath from './utils/buildAssetPath';
import { useObservations } from './store/observations';
import PlanAnalyzerPt2Section from './comps/sections/plan-analyzers-pt2-section';
import useDiagnostics from './hooks/useDiagnostics.hook';
import useDPI from './hooks/useDPI.hook';
import LowDPIAlert from './comps/LowDPIAlert';
import { useMessages } from './store/messages';

export const App = () => {
  const load = useObservations(({ load }) => load);

  const [sessionId, setSessionId] = useState<string>();
  const [ownTeamId, setOwnTeamId] = useState<string>();
  const [selectedTeamId, setSelectedTeamId] = useState<string>();
  const [fullpageApi, setFullpageApi] = useState<any>();

  const isLeftKeyPressed = useKeyPress(37);
  const isUpKeyPressed = useKeyPress(38);
  const isRightKeyPressed = useKeyPress(39);
  const isDownKeyPressed = useKeyPress(40);

  const t = useMessages(({ print }) => print);

  const dpi: number = useDPI();

  useDiagnostics();

  useEffect(() => {
    const parsed = qs.parse(location.search);
    const s = parsed?.session?.toString();
    if (!s) setSessionId('CU-TEST');
    else setSessionId(s);
    setOwnTeamId(parsed?.teamName?.toString());
  }, []);

  useEffect(() => {
    if (sessionId)
      load('main', `/sessions`)
        .then(() => console.log('data loaded'))
        .catch(error => console.error(error));
  }, [sessionId]);

  useEffect(() => {
    if (isDownKeyPressed || isRightKeyPressed)
      return fullpageApi.moveSectionDown();
    if (isUpKeyPressed || isLeftKeyPressed) return fullpageApi.moveSectionUp();
  }, [isLeftKeyPressed, isUpKeyPressed, isRightKeyPressed, isDownKeyPressed]);

  useEffect(() => handleTeamSelection(undefined), []);

  const handleTeamSelection = (teamId?: string) => {
    setSelectedTeamId(teamId);

    if (typeof window === 'undefined') return;

    if (teamId) fullpageApi.moveSectionDown();

    if (typeof document === 'undefined') return;

    document
      .querySelectorAll(
        '#fullpage > div.section.fp-section.active.fp-completely > div.fp-arrow.fp-controlArrow'
      )
      .forEach(arrow => ((arrow as any).style.opacity = teamId ? '0.1' : '0'));
  };

  const messages = useMessages(m => m);

  if (!sessionId)
    return (
      <p>No session id specified. Specify with "{`?session=<SESSION_ID>`}".</p>
    );

  return (
    <>
      <MessagesInitializer />
      <div
        className="relative bg-gray-200 bg-cover select-none cursor-cell"
        style={{
          backgroundImage: `url("${buildAssetPath(
            'images/background_static.webp'
          )}")`,
        }}
      >
        {dpi > 1.15 && <LowDPIAlert />}

        <button
          className="absolute z-50 p-2 m-2 bg-gray-200 rounded-md shadow-md top-4 left-4"
          onClick={() =>
            messages.changeIdiom(messages.idiom === 'en' ? 'it' : 'en')
          }
        >
          {messages.idiom}
        </button>

        <ReactFullpage
          licenseKey="24B9AF4F-DBD94873-8B587720-F358572F"
          scrollingSpeed={1_400}
          css3
          loopTop={false}
          loopBottom={false}
          loopHorizontal={false}
          scrollBar={false}
          fitToSection
          onLeave={(origin, destination, direction) => {
            if (destination.index < 5) handleTeamSelection(undefined);
          }}
          render={({ fullpageApi }) => {
            if (fullpageApi) setFullpageApi(fullpageApi);

            return (
              <ReactFullpage.Wrapper>
                <div className="section">
                  <div className="flex flex-col w-screen h-screen">
                    <div className="select-none w-full bg-transparent grid grid-cols-3 grid-rows-1 gap-x-8 px-8 lg:px-10 xl:px-12 2xl:px-16 py-2 z-50 h-[100px]">
                      <img
                        src={buildAssetPath('images/logo_bocconi.png')}
                        alt="Logo Bocconi"
                        className="my-auto mr-auto h-1/2 _nodrag"
                      />
                      <img
                        src={buildAssetPath('images/logo.png')}
                        alt="Logo Bocconi"
                        className="mx-auto my-auto h-1/2 _nodrag"
                        style={{ imageRendering: '-webkit-optimize-contrast' }}
                      />
                      <img
                        src={buildAssetPath('images/logo_built_internal.png')}
                        alt="Logo Bocconi"
                        className="my-auto ml-auto h-1/2 _nodrag"
                        style={{
                          filter: 'drop-shadow(2px 4px 6px black)',
                        }}
                      />
                    </div>
                    <h2 className="m-auto font-black text-center text-blue-100 text-7xl">
                      I Change U - Debriefing
                    </h2>
                  </div>
                </div>

                <div className="h-screen overflow-hidden section">
                  <ARBudgetByScenarioSection
                    sessionId={sessionId}
                    ownGroupId={ownTeamId}
                  />
                </div>

                <div className="section">
                  <PerformanceAdjustmentSection
                    sessionId={sessionId}
                    ownGroupId={ownTeamId}
                  />
                </div>

                <div className="section">
                  <DistanceDispersionSection
                    sessionId={sessionId}
                    groupId={selectedTeamId || ownTeamId}
                    handleTeamSelection={(teamId: string) => {
                      // Disable team reselection in student mode.
                      if (ownTeamId) return;

                      setSelectedTeamId(teamId);
                      fullpageApi?.moveTo(6, 0);
                    }}
                  />
                </div>

                <div className="section" data-anchor="teams">
                  <InView as="div" threshold={0.4} triggerOnce={false}>
                    <TeamSelector
                      sessionId={sessionId}
                      ownTeamId={ownTeamId}
                      handleTeamSelection={handleTeamSelection}
                    />
                  </InView>
                </div>

                <div className="section" data-anchor="team-plan">
                  <PlanAnalyzerSection
                    sessionId={sessionId}
                    groupId={selectedTeamId || ownTeamId}
                  />
                </div>

                <div className="section" data-anchor="team-plan-2">
                  <PlanAnalyzerPt2Section
                    sessionId={sessionId}
                    groupId={selectedTeamId || ownTeamId}
                  />
                </div>

                <div className="section" data-anchor="team-plan-3">
                  <div className="relative flex items-center justify-center w-screen h-screen">
                    <StepsQualitySection
                      sessionId={sessionId}
                      groupId={selectedTeamId || ownTeamId}
                    />
                  </div>
                </div>

                <div className="section" data-anchor="team-plan-4">
                  <div className="relative flex items-center justify-center w-screen h-screen">
                    <DebriefingSection
                      title={t('slide-9-title')}
                      subTitle={t('slide-9-tooltip')}
                    >
                      <Bho />
                    </DebriefingSection>
                  </div>
                </div>
              </ReactFullpage.Wrapper>
            );
          }}
        />

        <PresentationToolbar fullpageApi={fullpageApi} />
        <NavigationToolbar fullpageApi={fullpageApi} />
      </div>
    </>
  );
};
